/*
 * The purpose of this manager is so that the Cookiebot global events
 * are only bound once, regardless of how many components/instances
 * require the functionality.
 * It includes a `pendingActions` facility which queues callbacks
 * in case Cookiebot hasn't been initialised before the consumer
 * component requires it (Cookiebot is loaded asynchronously).
 * Subscribe() and unsubscribe() allow components to register
 * and remove callback functions.
 */

// Import Cookiebot types
declare global {
  interface Window {
    // Cookiebot object requires a capital C - hence disabling linter
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Cookiebot?: typeof Cookiebot;
  }
}

export interface CookieConsentCallbacks {
  onMarketingAccepted?: () => void;
  onMarketingDenied?: () => void;
}

class CookieConsentManager {
  private readonly callbacks: Set<CookieConsentCallbacks> = new Set();
  private readonly pendingActions: Array<() => void> = [];
  private initialised = false;
  private cookiebotReady = false;

  constructor() {
    this.init();
  }

  private init() {
    if (this.initialised || typeof window === "undefined") return;

    // Listen for Cookiebot load event
    window.addEventListener("CookiebotOnLoad", () => {
      this.cookiebotReady = true;
      this.processPendingActions();
      this.handleConsentChange();
    });

    // Listen for Cookiebot events
    window.addEventListener("CookiebotOnConsentReady", () => {
      this.cookiebotReady = true;
      this.processPendingActions();
      this.handleConsentChange();
    });

    window.addEventListener("CookiebotOnAccept", () => {
      this.handleConsentChange();
    });

    window.addEventListener("CookiebotOnDecline", () => {
      this.handleConsentChange();
    });

    this.initialised = true;

    // Check if Cookiebot is already loaded (in case we missed the event)
    if (this.isCookiebotReady()) {
      this.cookiebotReady = true;
      this.processPendingActions();
      this.handleConsentChange();
    }
  }

  private processPendingActions() {
    this.pendingActions.forEach(action => action());
    this.pendingActions.length = 0; // Clear the array
  }

  private isCookiebotReady(): boolean {
    return typeof window !== "undefined"
      && typeof window.Cookiebot !== "undefined"
      && !!window.Cookiebot?.consent;
  }

  private handleConsentChange() {
    if (!this.isCookiebotReady()) return;

    const hasMarketingConsent = this.hasMarketingConsent();

    this.callbacks.forEach((callback) => {
      if (hasMarketingConsent && callback.onMarketingAccepted) {
        callback.onMarketingAccepted();
      }
      else if (!hasMarketingConsent && callback.onMarketingDenied) {
        callback.onMarketingDenied();
      }
    });
  }

  public hasMarketingConsent(): boolean {
    if (!this.isCookiebotReady()) return false;
    return window.Cookiebot?.consent.marketing === true;
  }

  public acceptMarketingCookies(): void {
    if (!this.cookiebotReady || !this.isCookiebotReady()) {
      this.pendingActions.push(() => this.acceptMarketingCookies());
      return;
    }

    const cookiebot = window.Cookiebot!;

    // Submit custom consent with marketing enabled.
    // Maintain other cookie types (preferences and statistics).
    cookiebot.submitCustomConsent(
      cookiebot.consent.preferences,
      cookiebot.consent.statistics,
      true, // Marketing
    );
  }

  public subscribe(callbacks: CookieConsentCallbacks): void {
    this.callbacks.add(callbacks);

    // Immediately call appropriate callback based on current state
    if (this.isCookiebotReady()) {
      const hasMarketing = this.hasMarketingConsent();
      if (hasMarketing && callbacks.onMarketingAccepted) {
        callbacks.onMarketingAccepted();
      }
      else if (!hasMarketing && callbacks.onMarketingDenied) {
        callbacks.onMarketingDenied();
      }
    }
  }

  public unsubscribe(callbacks: CookieConsentCallbacks): void {
    this.callbacks.delete(callbacks);
  }
}

export const cookieConsentManager = new CookieConsentManager();
